import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Layout from "@/components/application/layout";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { get, post } from "@/lib/api";
import { usePage } from "@inertiajs/react";
import { SelectPlanPageProps } from "@/types";
import { Checkbox } from "@/components/ui/checkbox";
import { getStoreUrl } from "@/lib/utils";
import useTranslations from "@/hooks/use-translations";
import PaymentFields from "@/components/ui/culqi/payment-fields";
import { useQuery } from "@tanstack/react-query";
import { Spinner } from "@/components/ui/spinner";

type BillingInfo = {
  first_name: string;
  last_name: string;
  address: string;
  address_city: string;
  country_code: string;
  phone_number: string;
  accept_terms: boolean;
};

const emptyBillingInfo: BillingInfo = {
  first_name: "",
  last_name: "",
  address: "",
  address_city: "",
  country_code: "",
  phone_number: "",
  accept_terms: false,
};

function SelectPlanPage() {
  const currentPage = usePage<SelectPlanPageProps>();
  const { current_user, available_plans, culqi_public_key, countries } =
    currentPage.props;
  const { t } = useTranslations("site.select_plan");

  const [billingInfo, setBillingInfo] = useState<BillingInfo>(emptyBillingInfo);
  const [subscriptionRequestStatus, setSubscriptionRequestStatus] = useState<
    "started" | "done" | "failed" | null
  >(null);

  function updateField<K extends keyof BillingInfo>(
    field: K,
    value: BillingInfo[K]
  ) {
    setBillingInfo((values) => ({ ...values, [field]: value }));
  }

  const pollingQuery = useQuery({
    queryKey: ["subscription"],
    queryFn() {
      return get("billing/subscription");
    },
    refetchInterval(query) {
      if (query.state.data?.subscription) {
        return false;
      }

      return 5_000;
    },
    enabled: subscriptionRequestStatus === "done",
  });

  useEffect(() => {
    if (pollingQuery.data?.subscription?.status === "active") {
      location.href = getStoreUrl("admin");
    }
  }, [pollingQuery.data?.subscription?.status]);

  const planId = "basic";
  const plan = available_plans.find((plan) => plan.slug === planId);

  const subscribe = useCallback(
    async (tokenId: string, billingInfo: BillingInfo) => {
      try {
        if (!plan) {
          return;
        }

        setSubscriptionRequestStatus("started");

        await post("billing/subscribe", {
          plan_id: plan.id,
          token_id: tokenId,
          ...billingInfo,
        });

        setSubscriptionRequestStatus("done");
      } catch (error) {
        console.error(error);
        setSubscriptionRequestStatus("failed");
      }
    },
    [billingInfo]
  );

  return (
    <div className="w-full h-full grid grid-cols-2">
      <aside className="p-16 flex justify-center">
        <FieldSet>
          <FieldLegend>{t("legend")}</FieldLegend>
          <FieldDescription>{t("description")}</FieldDescription>

          <FieldGroup className="gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field>
                <FieldLabel htmlFor="first_name">
                  {t("first_name_label")}
                </FieldLabel>
                <Input
                  id="first_name"
                  value={billingInfo.first_name}
                  onChange={(event) =>
                    updateField("first_name", event.target.value)
                  }
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="last_name">
                  {t("last_name_label")}
                </FieldLabel>
                <Input
                  id="last_name"
                  value={billingInfo.last_name}
                  onChange={(event) =>
                    updateField("last_name", event.target.value)
                  }
                />
              </Field>
            </div>

            <Field>
              <FieldLabel htmlFor="address">{t("address_label")}</FieldLabel>
              <Input
                id="address"
                value={billingInfo.address}
                onChange={(event) => updateField("address", event.target.value)}
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field>
                <FieldLabel htmlFor="address_city">
                  {t("city_label")}
                </FieldLabel>
                <Input
                  id="address_city"
                  value={billingInfo.address_city}
                  onChange={(event) =>
                    updateField("address_city", event.target.value)
                  }
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="country_code">
                  {t("country_label")}
                </FieldLabel>
                <Select
                  value={billingInfo.country_code}
                  onValueChange={(value) => updateField("country_code", value)}
                >
                  <SelectTrigger id="country_code" className="w-full">
                    <SelectValue placeholder={t("country_placeholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country.code} value={country.code}>
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field>
              <FieldLabel htmlFor="phone_number">
                {t("phone_number_label")}
              </FieldLabel>
              <Input
                id="phone_number"
                type="tel"
                value={billingInfo.phone_number}
                onChange={(event) =>
                  updateField("phone_number", event.target.value)
                }
              />
            </Field>

            <Field orientation="horizontal">
              <Checkbox
                id="accept-terms"
                name="accept-terms"
                onCheckedChange={(checked) =>
                  updateField("accept_terms", checked === true)
                }
              />
              <FieldLabel
                htmlFor="accept-terms"
                dangerouslySetInnerHTML={{
                  __html: t("accept_terms_label_html"),
                }}
              />
            </Field>
          </FieldGroup>
        </FieldSet>
      </aside>
      <aside className="p-16 flex justify-center">
        <PaymentFields
          publicKey={culqi_public_key}
          email={current_user!.email}
          currency="USD"
          className="h-120 w-full"
          amountCents={plan?.amount_cents}
          title="BrickGestor"
          description={`Subscription to ${plan?.name}`}
          onComplete={(token) => subscribe(token, billingInfo)}
        />
      </aside>
      {subscriptionRequestStatus && (
        <div
          data-state="open"
          className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        >
          <Spinner className="size-8" />
        </div>
      )}
    </div>
  );
}

SelectPlanPage.layout = (page: ReactNode) => <Layout children={page} />;

export default SelectPlanPage;
